import { type BarcosRoute, type Comunidad, type TipoSegment } from '$lib/geo';
import {
	listingTypeFilterToPbClause,
	parseListingTypeFilter,
	type ListingTypeFilter
} from '$lib/listing-display';
import { CalendarDate } from '@internationalized/date';
import type { DateRange } from 'bits-ui';
import { SvelteMap } from 'svelte/reactivity';

export interface MarketplaceFilterState {
	location: string;
	province: string;
	dateRange: DateRange | undefined;
	boatType: 'velero' | 'motora' | null;
	listingType: ListingTypeFilter | null;
	/** Solo salidas compartidas (`kind = experience`); válido con `con_patron`. */
	experiencesOnly: boolean;
	passengersRange: [number, number];
	priceRange: [number, number];
}

export class MarketplaceFiltersState {
	private _location = $state<string>('');
	private _province = $state<string>('');
	// Ubicación derivada del path /barcos/{tipo}/{comunidad}/{provincia}/{ciudad}/{puerto}
	private _routeTipo = $state<TipoSegment | null>(null);
	private _comunidad = $state<Comunidad | null>(null);
	private _city = $state<string | null>(null);
	private _port = $state<{ id: string; name: string } | null>(null);
	private _dateRange = $state<DateRange | undefined>(undefined);
	private _boatType = $state<'velero' | 'motora' | null>(null);
	private _listingType = $state<ListingTypeFilter | null>(null);
	private _experiencesOnly = $state(false);
	private _passengersRange = $state<[number, number]>([1, 12]);
	private _priceRange = $state<[number, number]>([0, 2000]);

	private _isDirty = $state(false);
	private _appliedFilters = $state<MarketplaceFilterState | null>(null);

	private debounceTimers = new SvelteMap<string, NodeJS.Timeout>();
	private onFiltersChangeCallback?: (filters: string) => void;

	get location() {
		return this._location;
	}
	get province() {
		return this._province;
	}
	get routeTipo() {
		return this._routeTipo;
	}
	get comunidad() {
		return this._comunidad;
	}
	get city() {
		return this._city;
	}
	get port() {
		return this._port;
	}
	get dateRange() {
		return this._dateRange;
	}
	get boatType() {
		return this._boatType;
	}
	get listingType() {
		return this._listingType;
	}
	get experiencesOnly() {
		return this._experiencesOnly;
	}
	get passengersRange() {
		return this._passengersRange;
	}
	get priceRange() {
		return this._priceRange;
	}
	get isDirty() {
		return this._isDirty;
	}

	get dirtyCount() {
		let count = 0;
		if (this._appliedFilters) {
			if (this._location !== this._appliedFilters.location) count++;
			if (this._province !== this._appliedFilters.province) count++;
			if (JSON.stringify(this._dateRange) !== JSON.stringify(this._appliedFilters.dateRange))
				count++;
			if (this._boatType !== this._appliedFilters.boatType) count++;
			if (this._listingType !== this._appliedFilters.listingType) count++;
			if (this._experiencesOnly !== this._appliedFilters.experiencesOnly) count++;
			if (
				JSON.stringify(this._passengersRange) !==
				JSON.stringify(this._appliedFilters.passengersRange)
			)
				count++;
			if (JSON.stringify(this._priceRange) !== JSON.stringify(this._appliedFilters.priceRange))
				count++;
		} else {
			if (this._location) count++;
			if (this._province) count++;
			if (this._dateRange) count++;
			if (this._boatType) count++;
			if (this._listingType) count++;
			if (this._experiencesOnly) count++;
			if (this._passengersRange[0] !== 1 || this._passengersRange[1] !== 12) count++;
			if (this._priceRange[0] !== 0 || this._priceRange[1] !== 2000) count++;
		}
		return count;
	}

	setLocation(value: string) {
		this._location = value;
		this._isDirty = true;
	}

	setProvince(value: string) {
		this._province = value;
		this._isDirty = true;
		this.debouncedApply('province', 0);
	}

	setDateRange(value: DateRange | undefined) {
		this._dateRange = value;
		this._isDirty = true;
	}

	setBoatType(value: 'velero' | 'motora' | null) {
		this._boatType = value;
		this._isDirty = true;
		this.debouncedApply('boatType', 0);
	}

	setListingType(value: ListingTypeFilter | null) {
		this._listingType = value;
		if (value !== 'con_patron') {
			this._experiencesOnly = false;
		}
		this._isDirty = true;
		this.debouncedApply('listingType', 0);
	}

	setExperiencesOnly(value: boolean) {
		if (this._listingType !== 'con_patron') {
			this._experiencesOnly = false;
			return;
		}
		this._experiencesOnly = value;
		this._isDirty = true;
		this.debouncedApply('experiencesOnly', 0);
	}

	setPassengersRange(value: [number, number]) {
		this._passengersRange = value;
		this._isDirty = true;
		this.debouncedApply('passengers', 300);
	}

	setPriceRange(value: [number, number]) {
		this._priceRange = value;
		this._isDirty = true;
		this.debouncedApply('price', 300);
	}

	private debouncedApply(key: string, delay: number) {
		const existingTimer = this.debounceTimers.get(key);
		if (existingTimer) clearTimeout(existingTimer);

		if (delay === 0) {
			this.applyFilters();
		} else {
			const timer = setTimeout(() => {
				this.applyFilters();
				this.debounceTimers.delete(key);
			}, delay);
			this.debounceTimers.set(key, timer);
		}
	}

	applyFilters() {
		this._appliedFilters = {
			location: this._location,
			province: this._province,
			dateRange: this._dateRange,
			boatType: this._boatType,
			listingType: this._listingType,
			experiencesOnly: this._experiencesOnly,
			passengersRange: [...this._passengersRange] as [number, number],
			priceRange: [...this._priceRange] as [number, number]
		};
		this._isDirty = false;

		if (this.onFiltersChangeCallback) {
			this.onFiltersChangeCallback(this.buildFilterQuery());
		}

		return this._appliedFilters;
	}

	buildFilterQuery(): string {
		const filters: string[] = ['status = "active"'];
		const dateRangePrefix = 'listingdateranges_via_listing';

		const kindClause = listingTypeFilterToPbClause(this._listingType, {
			experiencesOnly: this._listingType === 'con_patron' && this._experiencesOnly
		});
		if (kindClause) filters.push(kindClause);

		if (this._routeTipo === 'experiencia') {
			filters.push('kind = "experience"');
		} else if (this._routeTipo === 'alquiler') {
			filters.push('kind != "experience"');
		}

		if (this._port) {
			filters.push(`boat.port = "${this._port.id}"`);
		} else if (this._city) {
			filters.push(`boat.port.city = "${this._city}"`);
			if (this._province) {
				filters.push(`boat.port.province = "${this._province}"`);
			}
		} else if (this._province) {
			filters.push(`boat.port.province ~ "${this._province}"`);
		} else if (this._comunidad) {
			const clause = this._comunidad.provinces
				.map((province) => `boat.port.province = "${province}"`)
				.join(' || ');
			filters.push(`(${clause})`);
		} else if (this._location) {
			filters.push(
				`(boat.port.name ~ "${this._location}" || boat.port.province ~ "${this._location}")`
			);
		}

		if (this._boatType) {
			const boatKind = this._boatType === 'velero' ? 'sailboat' : 'motorboat';
			filters.push(`boat.kind = "${boatKind}"`);
		}

		if (this._passengersRange) {
			filters.push(`spots >= ${this._passengersRange[0]} && spots <= ${this._passengersRange[1]}`);
		}

		const isDefaultPriceRange =
			this._priceRange[0] === 0 && this._priceRange[1] === 2000;
		if (!isDefaultPriceRange) {
			filters.push(
				`${dateRangePrefix}.price ?>= ${this._priceRange[0]} && ${dateRangePrefix}.price ?<= ${this._priceRange[1]}`
			);
		}

		if (this._dateRange?.start && this._dateRange?.end) {
			const startDateStr = this._dateRange.start.toString();
			const endDateStr = this._dateRange.end.toString();
			const startDate = `${startDateStr} 00:00:00`;
			const endDate = `${endDateStr} 23:59:59`;
			filters.push(
				`${dateRangePrefix}.startDate ?<= "${endDate}" && ${dateRangePrefix}.endDate ?>= "${startDate}"`
			);
		}

		return filters.join(' && ');
	}

	reset() {
		this._location = '';
		this._province = '';
		this._routeTipo = null;
		this._comunidad = null;
		this._city = null;
		this._port = null;
		this._boatType = null;
		this._listingType = null;
		this._experiencesOnly = false;
		this._passengersRange = [1, 12];
		this._priceRange = [0, 2000];
		this._dateRange = undefined;
		this._isDirty = false;
		this._appliedFilters = null;

		this.debounceTimers.forEach((timer) => clearTimeout(timer));
		this.debounceTimers.clear();

		if (this.onFiltersChangeCallback) {
			this.onFiltersChangeCallback(this.buildFilterQuery());
		}
	}

	onFiltersChange(callback: (filters: string) => void) {
		this.onFiltersChangeCallback = callback;
	}

	toURLParams(): URLSearchParams {
		const params = new URLSearchParams();

		if (this._location) params.set('location', this._location);
		if (this._province) params.set('province', this._province);
		if (this._listingType) params.set('tipo', this._listingType);
		if (this._listingType === 'con_patron' && this._experiencesOnly) {
			params.set('experiencias', '1');
		}
		if (this._boatType) params.set('barco', this._boatType);
		if (this._passengersRange[0] !== 1 || this._passengersRange[1] !== 12) {
			params.set('passengers', `${this._passengersRange[0]}-${this._passengersRange[1]}`);
		}
		if (this._priceRange[0] !== 0 || this._priceRange[1] !== 2000) {
			params.set('price', `${this._priceRange[0]}-${this._priceRange[1]}`);
		}
		if (this._dateRange?.start && this._dateRange?.end) {
			params.set('from', this._dateRange.start.toString());
			params.set('to', this._dateRange.end.toString());
		}

		return params;
	}

	fromURLParams(params: URLSearchParams) {
		const location = params.get('location');
		if (location) this._location = location;

		const province = params.get('province');
		if (province) this._province = province;

		const tipoParam = params.get('tipo');
		if (tipoParam === 'experience') {
			this._listingType = 'con_patron';
			this._experiencesOnly = true;
		} else {
			this._listingType = parseListingTypeFilter(tipoParam);
			this._experiencesOnly =
				this._listingType === 'con_patron' &&
				(params.get('experiencias') === '1' || params.get('solo_experiencias') === '1');
		}

		const barco = params.get('barco');
		if (barco === 'velero' || barco === 'motora') {
			this._boatType = barco;
		}

		const passengers = params.get('passengers');
		if (passengers) {
			const [min, max] = passengers.split('-').map(Number);
			if (!isNaN(min) && !isNaN(max)) {
				this._passengersRange = [min, max];
			}
		}

		const price = params.get('price');
		if (price) {
			const [min, max] = price.split('-').map(Number);
			if (!isNaN(min) && !isNaN(max)) {
				this._priceRange = [min, max];
			}
		}

		const from = params.get('from');
		const to = params.get('to');
		if (from && to) {
			try {
				const [fromYear, fromMonth, fromDay] = from.split('-').map(Number);
				const [toYear, toMonth, toDay] = to.split('-').map(Number);
				this._dateRange = {
					start: new CalendarDate(fromYear, fromMonth, fromDay),
					end: new CalendarDate(toYear, toMonth, toDay)
				};
			} catch {
				// ignore invalid dates
			}
		}

		this._appliedFilters = {
			location: this._location,
			province: this._province,
			dateRange: this._dateRange,
			boatType: this._boatType,
			listingType: this._listingType,
			experiencesOnly: this._experiencesOnly,
			passengersRange: [...this._passengersRange] as [number, number],
			priceRange: [...this._priceRange] as [number, number]
		};
		this._isDirty = false;
	}

	/**
	 * Sincroniza la ubicación y el tipo desde los segmentos del path
	 * /barcos/... sin marcar dirty. `cityName` y `port` llegan ya resueltos
	 * desde los slugs contra la colección de puertos.
	 */
	setRouteFromPath(
		route: BarcosRoute | null,
		cityName: string | null = null,
		port: { id: string; name: string } | null = null
	) {
		this._routeTipo = route?.tipo ?? null;
		this._comunidad = route?.comunidad ?? null;
		this._city = route?.ciudadSlug ? cityName : null;
		this._port = route?.puertoSlug ? port : null;
		if (route?.comunidad) {
			this._province = route.provincia ?? '';
			this._location = '';
		} else if (route) {
			this._province = '';
		}
		this._appliedFilters = {
			location: this._location,
			province: this._province,
			dateRange: this._dateRange,
			boatType: this._boatType,
			listingType: this._listingType,
			experiencesOnly: this._experiencesOnly,
			passengersRange: [...this._passengersRange] as [number, number],
			priceRange: [...this._priceRange] as [number, number]
		};
		this._isDirty = false;
	}

	/** Aplica tipo de anuncio desde URL sin marcar dirty (p. ej. redirect legacy). */
	setListingTypeFromRoute(tipo: ListingTypeFilter | null, experiencesOnly = false) {
		this._listingType = tipo;
		this._experiencesOnly = tipo === 'con_patron' && experiencesOnly;
		this._appliedFilters = {
			location: this._location,
			province: this._province,
			dateRange: this._dateRange,
			boatType: this._boatType,
			listingType: this._listingType,
			experiencesOnly: this._experiencesOnly,
			passengersRange: [...this._passengersRange] as [number, number],
			priceRange: [...this._priceRange] as [number, number]
		};
		this._isDirty = false;
	}

	destroy() {
		this.debounceTimers.forEach((timer) => clearTimeout(timer));
		this.debounceTimers.clear();
	}
}

let marketplaceFilters: MarketplaceFiltersState;

export function getMarketplaceFilters(): MarketplaceFiltersState {
	if (!marketplaceFilters) {
		marketplaceFilters = new MarketplaceFiltersState();
	}
	return marketplaceFilters;
}
